import * as React from 'react';
import { createPortal } from 'react-dom';
const { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } = React;
import type { SelectInputPayload, SelectInputOption } from '../types/assistant';

interface AssistantSelectInputProps {
  payload: SelectInputPayload;
  onSelect: (option: SelectInputOption) => void;
  disabled?: boolean;
}

interface DropdownPosition {
  left: number;
  width: number;
  top: number;
  maxHeight: number;
  direction: 'up' | 'down';
}

const wrapperStyle: React.CSSProperties = {
  position: 'relative',
  padding: '4px 0 8px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  fontFamily: 'inherit',
  color: '#2C5282',
  background: '#fff',
  border: '1px solid #bee3f8',
  borderRadius: 8,
  outline: 'none',
  boxSizing: 'border-box',
};

const dropdownBaseStyle: React.CSSProperties = {
  position: 'fixed',
  overflowY: 'auto',
  background: '#fff',
  border: '1px solid #bee3f8',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  zIndex: 10010,
};

const DROPDOWN_GAP = 4;
const DROPDOWN_MIN_HEIGHT = 120;
const DROPDOWN_MAX_HEIGHT = 280;
const VIEWPORT_MARGIN = 8;

const optionStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 13,
  fontFamily: 'inherit',
  color: '#2C5282',
  cursor: 'pointer',
  borderBottom: '1px solid #ebf8ff',
};

const emptyStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 12,
  color: '#a0aec0',
  fontStyle: 'italic',
};

export const AssistantSelectInput: React.FC<AssistantSelectInputProps> = ({ payload, onSelect, disabled }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return payload.options.slice(0, 50);
    return payload.options.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 50);
  }, [query, payload.options]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  const measure = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceAbove = rect.top - VIEWPORT_MARGIN;
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
    const openUp = spaceAbove > spaceBelow;
    const available = Math.max(openUp ? spaceAbove : spaceBelow, DROPDOWN_MIN_HEIGHT);
    const maxHeight = Math.min(DROPDOWN_MAX_HEIGHT, available - DROPDOWN_GAP);
    setPosition({
      left: rect.left,
      width: rect.width,
      top: openUp ? Math.max(VIEWPORT_MARGIN, rect.top - maxHeight - DROPDOWN_GAP) : rect.bottom + DROPDOWN_GAP,
      maxHeight,
      direction: openUp ? 'up' : 'down',
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, measure]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inWrapper = wrapperRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inWrapper && !inDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const pick = (opt: SelectInputOption) => {
    if (disabled) return;
    onSelect(opt);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) pick(opt);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const dropdown = open && position && typeof document !== 'undefined' ? createPortal(
    (
      <div
        ref={dropdownRef}
        style={{
          ...dropdownBaseStyle,
          left: position.left,
          width: position.width,
          top: position.top,
          maxHeight: position.maxHeight,
        }}
      >
        {filtered.length === 0 ? (
          <div style={emptyStyle}>No matches</div>
        ) : (
          filtered.map((opt, i) => (
            <div
              key={`${opt.label}-${i}`}
              style={{
                ...optionStyle,
                background: i === highlight ? '#ebf8ff' : '#fff',
              }}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(opt);
              }}
            >
              {opt.label}
            </div>
          ))
        )}
      </div>
    ),
    document.body
  ) : null;

  return (
    <div ref={wrapperRef} style={wrapperStyle}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        placeholder={payload.placeholder}
        style={{ ...inputStyle, opacity: disabled ? 0.6 : 1 }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {dropdown}
    </div>
  );
};
